import { useFormattedDate } from '@common/hooks/formatters/useFormattedDate';
import { flattenObjectPaths } from '@common/utils/flattenObjectPath';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDownIcon, ClockIcon, PersonIcon } from '@radix-ui/react-icons';
import { Box, Card, Flex, Grid, IconButton, Text } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { OverrideHistoryItem } from '../types';
import { HistoryFormattedValue } from './HistoryFormattedValue';

import styles from './HistoryItemCard.module.css';
import { HistoryAuthorLabel } from './HistoryAuthorLabel';

const getTranslationKey = (rawKey: string) => {
    return rawKey.replace(/\.(fr|en|de|es|it)(?=\.|$)/g, '');
};

const checkIsLongData = (val: unknown): boolean => {
    if (typeof val === 'string') return val.length > 60;
    if (Array.isArray(val)) return val.join(' ').length > 40;
    return false;
};

interface HistoryItemCardProps {
    item: OverrideHistoryItem;
}

export const HistoryItemCard = ({ item }: HistoryItemCardProps) => {
    const { t } = useTranslation('metadataEditor');
    const { formatDate } = useFormattedDate({
        dateStyle: 'medium',
        timeStyle: 'short',
    });

    const parsedData =
        typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
    const flatChanges = flattenObjectPaths(parsedData || {});
    const modifiedKeys = Object.keys(flatChanges);
    const modifiedFieldsCount = modifiedKeys.length;

    if (modifiedFieldsCount === 0) return null;

    return (
        <Card size='2' variant='surface'>
            <Collapsible.Root>
                <Collapsible.Trigger asChild>
                    <Box style={{ cursor: 'pointer' }}>
                        <Flex direction='column' gap='3'>
                            <Flex justify='between' align='center'>
                                <Flex align='center' gap='2'>
                                    <PersonIcon />
                                    <HistoryAuthorLabel userId={item.author} />
                                </Flex>
                                <Flex align='center' gap='2'>
                                    <ClockIcon />
                                    <Text size='1'>
                                        {formatDate(item.timestamp)}
                                    </Text>
                                </Flex>
                            </Flex>

                            <Flex
                                justify='between'
                                align='center'
                                style={{
                                    borderTop: '1px solid var(--gray-a4)',
                                    paddingTop: 'var(--space-2)',
                                }}
                            >
                                <Text size='2' color='gray' as='div'>
                                    {t('history.modifiedFields', {
                                        count: modifiedFieldsCount,
                                    })}
                                </Text>

                                <IconButton
                                    size='1'
                                    variant='ghost'
                                    color='gray'
                                    asChild
                                >
                                    <Box>
                                        <ChevronDownIcon
                                            className={styles.chevron}
                                        />
                                    </Box>
                                </IconButton>
                            </Flex>
                        </Flex>
                    </Box>
                </Collapsible.Trigger>

                <Collapsible.Content className={styles.content}>
                    <Box
                        mt='3'
                        pt='3'
                        style={{ borderTop: '1px solid var(--gray-a3)' }}
                    >
                        <Flex direction='column' gap='4'>
                            {modifiedKeys.map((key) => {
                                const cleanKey = getTranslationKey(key);
                                const value = flatChanges[key];

                                const isLongData = checkIsLongData(value);

                                return (
                                    <Grid
                                        key={key}
                                        columns={isLongData ? '1' : '120px 1fr'}
                                        gap={isLongData ? '1' : '3'}
                                        align='start'
                                    >
                                        <Text
                                            size='2'
                                            weight='bold'
                                            color='gray'
                                        >
                                            {t(`fields.${cleanKey}`, {
                                                defaultValue: cleanKey,
                                            })}
                                        </Text>

                                        <Flex
                                            justify={
                                                isLongData ? 'start' : 'end'
                                            }
                                            style={{
                                                textAlign: isLongData
                                                    ? 'left'
                                                    : 'right',
                                                overflowWrap: 'break-word',
                                                wordBreak: 'break-word',
                                                width: '100%',
                                            }}
                                        >
                                            <HistoryFormattedValue
                                                fieldKey={key}
                                                value={value}
                                            />
                                        </Flex>
                                    </Grid>
                                );
                            })}
                        </Flex>
                    </Box>
                </Collapsible.Content>
            </Collapsible.Root>
        </Card>
    );
};
